"use client";

import { createContext, useContext } from "react";

import type { LandingDesktopDownloadMenu } from "@/app/components/public/landing/LandingDesktopNavigation";
import type { LandingFilmId } from "@/src/features/landing/filmLibrary";

export type LandingLanguageOption = {
  value: string;
  label: string;
};

export type LandingLanguageWindowData = {
  current: string;
  options: LandingLanguageOption[];
  /** Resolves false when the switch failed, so the picker can un-light the row. */
  onSelect: (code: string) => boolean | Promise<boolean>;
};

export type LandingDownloadWindowData = {
  menu: LandingDesktopDownloadMenu;
  /** A failed download, shown inline in the panel instead of the page banner. */
  error: string | null;
};

export type LandingVideoWindowData = {
  /** The film the Videos window plays. */
  film: LandingFilmId;
  /** Opens the Videos window on this film (Pricing's button, for one). */
  open: (film: LandingFilmId) => void;
  /** Switches the open window to another film from its list. */
  select: (film: LandingFilmId) => void;
};

export type LandingNavWindowsData = {
  language: LandingLanguageWindowData;
  download: LandingDownloadWindowData | null;
  video: LandingVideoWindowData;
};

/**
 * The public Grid renders windows from serializable store props, so the live
 * language, download and video state (and their handlers) reach the landing
 * windows through this context instead of the isolated landing store.
 */
export const LandingNavWindowsContext = createContext<LandingNavWindowsData | null>(null);

export function useLandingNavWindows(): LandingNavWindowsData | null {
  return useContext(LandingNavWindowsContext);
}

/** The language's own name, written in that language ("Español", "日本語"). */
export function getNativeLanguageName(code: string, fallback = code): string {
  try {
    const name = new Intl.DisplayNames([code], { type: "language" }).of(code);
    if (!name) return fallback;
    return name.charAt(0).toLocaleUpperCase(code) + name.slice(1);
  } catch {
    return fallback;
  }
}

/** The language's English name, for visitors scanning in English. */
export function getEnglishLanguageName(code: string, fallback = code): string {
  try {
    return new Intl.DisplayNames(["en"], { type: "language" }).of(code) ?? fallback;
  } catch {
    return fallback;
  }
}
